import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useRef, useState } from "react";
import { useMerchant } from "@/hooks/useMerchant";
import { useProtectTRPC } from "../trpc";
import { submitFailure } from "./submitErrors";
import type { SubmitPayload, SubmitResult } from "./types";
import { useInductionPreApproved } from "./useInductionPreApproved";
import { INDUCTION_QUOTE_KEY } from "./useInductionQuote";
import { useInvalidateInduction } from "./useInvalidateInduction";

export type SubmitOutcome =
  | { status: "ok"; result: SubmitResult }
  // The server refused and created nothing. `orderedIds` is the list as it is
  // now, or undefined if it could not be fetched.
  | {
      status: "rejected";
      failure: "quoteChanged" | "screeningsUnavailable";
      orderedIds?: string[];
    }
  // No answer we can trust: the batch may or may not exist.
  | { status: "unknown" };

// Runs `submit` with an idempotency key that follows the payload: the same
// payload after an unknown outcome reuses the key (so the server returns the
// batch it may already have made); any new payload, or a refusal by the
// server, gets a new one.
export function useInductionSubmit() {
  const trpc = useProtectTRPC();
  const queryClient = useQueryClient();
  const { merchant } = useMerchant();
  const invalidate = useInvalidateInduction();
  const { refresh } = useInductionPreApproved();
  const { mutateAsync } = useMutation(trpc.induction.submit.mutationOptions());

  const attempt = useRef<{ fingerprint: string; key: string } | null>(null);
  const busy = useRef(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isUnknown, setIsUnknown] = useState(false);

  const submit = async (
    payload: SubmitPayload
  ): Promise<SubmitOutcome | undefined> => {
    if (!merchant?.id || busy.current) return undefined;
    busy.current = true;
    setIsSubmitting(true);

    const fingerprint = JSON.stringify(payload);
    const current =
      attempt.current?.fingerprint === fingerprint
        ? attempt.current
        : { fingerprint, key: crypto.randomUUID() };
    attempt.current = current;

    try {
      const result = await mutateAsync({
        rentalsMerchantId: merchant.id,
        idempotencyKey: current.key,
        ...payload,
      });
      void invalidate();
      setIsUnknown(false);
      return { status: "ok", result };
    } catch (error) {
      const failure = submitFailure(error);

      if (failure === "unknown") {
        setIsUnknown(true);
        return { status: "unknown" };
      }

      // The server answered: this key is spent, the next payload gets another.
      attempt.current = null;
      if (failure === "keyReused") {
        setIsUnknown(true);
        return { status: "unknown" };
      }

      // The numbers or the rows changed under the merchant: bring both up to
      // date. No ids come with the error, so the caller compares the list.
      queryClient.removeQueries({ queryKey: INDUCTION_QUOTE_KEY });
      const orderedIds = await refresh()
        .then(({ screenings }) => screenings.map((s) => s.screeningId))
        .catch(() => undefined);
      setIsUnknown(false);
      return { status: "rejected", failure, orderedIds };
    } finally {
      busy.current = false;
      setIsSubmitting(false);
    }
  };

  return { submit, isSubmitting, isUnknown };
}
